import { Button, Form, Input, InputNumber, message, Space, Select } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { serverFetch } from "@server";
import { fmtDateTimeFull } from "@extensions/utils";

const LicenseOrderMain = ({ companyId, accId, onUpdate, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [prices, setPrices] = useState([]);
  const [programs, setPrograms] = useState([]);
  const user = useSelector(s => s.user);

  const isCreate = accId === 0;
  const findSelectedPrice = () => prices.find(x => (x.duration ?? x.Duration) === item?.licDuration);
  const minQty = () => {
    const p = findSelectedPrice();
    return p ? (p.qtyMin ?? p.QtyMin ?? 1) : 1;
  };
  const maxQty = () => {
    const p = findSelectedPrice();
    return p ? (p.qtyMax ?? p.QtyMax ?? undefined) : undefined;
  };

  const findPriceFor = (draft) => findPriceForPriceList(draft, prices);

  const recalcSum = (draft, applyForm = true, pricesList = null) => {
    if (!isCreate) return draft;
    const pricesToUse = pricesList !== null ? pricesList : prices;
    const priceRule = findPriceForPriceList(draft, pricesToUse);
    const unit = priceRule ? (priceRule.price ?? priceRule.Price ?? 0) : 0;
    const summ = +(Number(unit) * Number(draft?.qty || 0)).toFixed(2);
    const next = { ...draft, summ };
    applyForm && form.setFieldsValue({ summ });
    return next;
  };

  const findPriceForPriceList = (draft, pricesList) => {
    if (!draft?.licDuration || !draft?.qty) return undefined;
    const duration = draft.licDuration;
    const qty = draft.qty;
    const byDuration = pricesList.filter(p => (p.duration ?? p.Duration) === duration);
    if (!byDuration.length) return undefined;
    // выбрать правило по диапазону количества, иначе первое по длительности
    return byDuration.find(p => (qty >= (p.qtyMin ?? p.QtyMin ?? 1)) && (p.qtyMax ?? p.QtyMax ? qty <= (p.qtyMax ?? p.QtyMax) : true)) || byDuration[0];
  };

  const getLicenses = () => {
    if (!item?.idProg) return;
    serverFetch(`dict/license?idProg=${item?.idProg}`)
      .then(data => setLicenses(data))
      .catch(e => message.error(e.userMessage));
  };

  const getPrices = () => {
    if (!isCreate) return;
    if (!item?.idProg || !item?.idLic) {
      setPrices([]);
      return;
    }
    serverFetch(`dict/price?progId=${item.idProg}&licId=${item.idLic}`)
      .then(data => {
        const newPrices = data.filter(p => p.active);
        setPrices(newPrices);
        if (!item?.licDuration && newPrices.length > 0) {
          const first = newPrices[0];
          const duration = first.duration ?? first.Duration;
          const minQty = first.qtyMin ?? first.QtyMin ?? 1;
          let nextItem = { ...item, licDuration: duration, qty: Math.max(1, minQty) };
          nextItem = recalcSum(nextItem, true, newPrices);
          setItem(nextItem);
          form.setFieldsValue({ licDuration: duration, qty: nextItem.qty, summ: nextItem.summ });
        }
      })
      .catch(e => message.error(e.userMessage));
  };
  // Получить доступные программы
  const getPrograms = () => {
    serverFetch(`dict/prog`)
      .then(data => setPrograms(data.filter(i => i.progCompany == process.env.REACT_APP_PROG_COMPANY)))
      .catch(e => message.error(e.userMessage));
  };

  const getData = () => {
    if (!companyId) return;
    if (isCreate) {
      // Создание нового заказа
      const initial = { companyId, accId: 0, qty: 1 };
      setItem(initial);
      form.setFieldsValue(initial);
      return;
    }
    setLoading(true);
    serverFetch(`license/order?companyId=${companyId}&accId=${accId}`)
      .then(data => {
        setItem(data);
        form.setFieldsValue(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  useEffect(() => {
    getPrograms();
    if (companyId) getData();
  }, [companyId, accId]);

  useEffect(() => { getLicenses(); }, [item?.idProg]);
  useEffect(() => { getPrices(); }, [item?.idProg, item?.idLic]);

  const onValuesChange = (changedValues) => {
    const key = Object.keys(changedValues)[0];
    let next = { ...item, ...changedValues };
    if (key === 'idProg') {
      next = { ...next, idLic: undefined, licDuration: undefined, qty: 1, summ: undefined };
      setLicenses([]);
      setPrices([]);
    }
    if (key === 'idLic') {
      next = { ...next, licDuration: undefined, qty: 1, summ: undefined };
      setPrices([]);
    }
    if (key === 'licDuration') {
      // при смене срока, откорректировать qty в границах
      const min = minQty();
      const max = maxQty();
      let q = next.qty || 1;
      if (min != null && q < min) q = min;
      if (max != null && q > max) q = max;
      next.qty = q;
      form.setFieldsValue({ qty: q });
      next = recalcSum(next);
    }
    if (key === 'qty') {
      // клампинг количества и перерасчет суммы
      const min = minQty();
      const max = maxQty();
      let q = Number(next.qty || 1);
      if (min != null && q < min) q = min;
      if (max != null && q > max) q = max;
      next.qty = q;
      form.setFieldsValue({ qty: q });
      next = recalcSum(next);
    }
    setItem(next);
  };

  const onSave = () => {
    setLoading(true);
    const method = isCreate ? 'POST' : 'PUT';
    serverFetch(`license/order`, { method, bodyData: item })
      .then((resp) => {
        setLoading(false);
        message.success(isCreate ? 'Заказ создан' : 'Заказ обновлен');
        onUpdate && onUpdate(resp || item);
        onClose && onClose(true);
      })
      .catch(e => { setLoading(false); message.error(e.userMessage); });
  };

  return (
    <div style={{ maxWidth: "90%" }}>
      <Form form={form} onValuesChange={onValuesChange} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} colon={false} disabled={loading} style={{ maxWidth: 600 }}>
        <Form.Item label="#"><Input value={item?.accId} disabled /></Form.Item>
        <Form.Item label="Компания"><Input value={item?.companyName} disabled /></Form.Item>
        <Form.Item label="Дата создания"><Input value={fmtDateTimeFull(item?.createDate)} disabled /></Form.Item>
        {accId === 0 || user?.admin ? (
          <Form.Item label="Программа" name="idProg">
            <Select placeholder="Выберите программу" options={programs.map(p => ({ value: p.id, label: p.name }))} />
          </Form.Item>
        ) : (
          <Form.Item label="Программа"><Input value={item?.progName} disabled /></Form.Item>
        )}
        <Form.Item label="Лицензия" name="idLic">
          <Select
            placeholder="Выберите лицензию"
            options={licenses.map(license => ({
              value: license.idLic,
              label: license.name
            }))}
          />
        </Form.Item>
        {isCreate ? (
          <Form.Item label="Срок лицензии" name="licDuration">
            <Select
              placeholder="Выберите срок"
              options={prices.map(pr => ({
                value: pr.duration,
                label: `${pr.duration} дней`,
              }))}
            />
          </Form.Item>
        ) : (
          <Form.Item label="Срок лицензии" name="licDuration"><InputNumber min={1} style={{ width: '100%' }} disabled /></Form.Item>
        )}
        <Form.Item label="Количество" name="qty">
          <InputNumber
            min={minQty()}
            max={maxQty()}
            style={{ width: '100%' }}
            disabled={!isCreate}
          />
        </Form.Item>
        <Form.Item label="Сумма" name="summ"><InputNumber min={0} step={0.01} style={{ width: '100%' }} disabled /></Form.Item>
        <Form.Item label="Кодовое слово" name="keyWord"><Input disabled /></Form.Item>
        <Form.Item label="Комментарий" name="komment"><Input.TextArea rows={3} disabled={accId === 0} /></Form.Item>
        <Form.Item label=" ">
          <Space>
            <Button type="primary" onClick={onSave} loading={loading} disabled={isCreate && (!item?.summ || item.summ === 0)}>Сохранить</Button>
            <Button onClick={() => onClose && onClose(false)}>Отмена</Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default LicenseOrderMain;
